import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const apiBase = () => {
  const { hostname, port, protocol } = window.location;
  if (!port || port === '80' || port === '443') return `${protocol}//${hostname}/api/v1`;
  if (port === '3000') return `http://${hostname}:8000/api/v1`;
  return `http://${hostname}:${port}/api/v1`;
};

function decodeJwt(token) {
  try {
    const p = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(
      decodeURIComponent(atob(p).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''))
    );
  } catch (e) { return {}; }
}

const fmtPrice = (n, c) => {
  if (n == null) return '';
  return `${c || 'USD'} ${Number(n).toLocaleString('en-US')}`;
};

const sessionKey = (slug) => `oh_session_${slug}`;

const I18N = {
  en: {
    loading: 'Loading…',
    notFound: 'Open house not found',
    licenseTitle: 'Licensed Real Estate Agent',
    licenseSub: "Tap to view {name}'s license ↗",
    regTitle: 'Unlock full home details + AI assistant',
    regSub: 'Sign in with Google and leave your phone number to instantly ask anything about this home.',
    signedAs: '✓ Signed in as {email}',
    namePh: 'Your name',
    emailPh: 'Email',
    phonePh: 'Phone number (so the agent can reach you)',
    registering: 'Registering…',
    registerBtn: 'Register & Unlock Home Info',
    phoneRequired: 'Please enter your phone number',
    signInRequired: 'Please sign in with Google or enter your email',
    registerFail: 'Registration failed, please try again',
    consent: 'By registering, you agree that agent {name} may contact you by phone or email about this home.',
    unlocked: '✓ Unlocked — ask the AI anything about this home',
    welcomeBack: '✓ Welcome back, {name} — ask the AI anything about this home',
    signout: 'Not you? Sign out',
    sessionExpired: 'Session expired — please register again to continue.',
    highlights: 'Home Highlights',
    photos: 'Photos',
    contact: 'Contact & More Info',
    redfin: 'View full Redfin listing ↗',
    igPost: '📸 Home on Instagram ↗',
    video: '🎬 Agent intro video ↗',
    call: '📞 Call {name}',
    chatTitle: '💬 Home AI Assistant',
    chatEmpty: 'Ask me anything about this home, e.g. "What is the HOA fee?" "Is there parking?" "Which schools are nearby?"',
    chatPh: 'Type your question…',
    send: 'Send',
    botBusy: 'The assistant is temporarily unavailable',
    botError: 'Sorry, please try again later',
    footer: 'Provided by {name} via HouseAI · Info is used only for this open house follow-up',
    ohLabel: 'Open House',
    specBeds: '{n} bd',
    specBaths: '{n} ba',
    specSqft: '{n} sq ft',
  },
  zh: {
    loading: '載入中…',
    notFound: '找不到此 open house',
    licenseTitle: '持照房地產經紀人',
    licenseSub: '點擊查看 {name} 的證照 ↗',
    regTitle: '免費解鎖房屋詳情 + AI 問答 bot',
    regSub: '用 Google 登入並留下手機號碼，即可立刻詢問這間房子的任何問題。',
    signedAs: '✓ 已登入：{email}',
    namePh: '你的名字',
    emailPh: 'Gmail 信箱',
    phonePh: '手機號碼（agent 才能聯絡你）',
    registering: '註冊中…',
    registerBtn: '註冊並解鎖房屋資訊',
    phoneRequired: '請輸入手機號碼',
    signInRequired: '請先登入 Google 或填寫 Email',
    registerFail: '註冊失敗，請重試',
    consent: '註冊即代表同意 agent {name} 以電話／Email 與你聯絡此房屋事宜。',
    unlocked: '✓ 已解鎖 — 歡迎隨時向 AI 詢問這間房子',
    welcomeBack: '✓ {name}，歡迎回來 — 隨時向 AI 詢問這間房子',
    signout: '不是你？登出',
    sessionExpired: '登入已過期，請重新註冊後繼續。',
    highlights: '房屋亮點',
    photos: '現場照片',
    contact: '聯絡與更多資訊',
    redfin: '查看 Redfin 完整掛牌 ↗',
    igPost: '📸 房屋 Instagram 貼文 ↗',
    video: '🎬 Agent 介紹影片 ↗',
    call: '📞 打給 {name}',
    chatTitle: '💬 房屋問答 AI',
    chatEmpty: '問我任何關於這間房子的問題，例如：「HOA 月費多少？」「有沒有車位？」「附近有哪些學校？」',
    chatPh: '輸入你的問題…',
    send: '送出',
    botBusy: 'bot 暫時無法回應',
    botError: '抱歉，稍後再試',
    footer: '由 {name} 透過 HouseAI 提供 · 註冊資訊僅供本屋 open house 聯絡使用',
    ohLabel: '開放看房時間',
    specBeds: '{n} 房',
    specBaths: '{n} 衛',
    specSqft: '{n} 平方呎',
  },
};

export default function OpenHouseKioskPage() {
  const { slug } = useParams();
  const [lang, setLang] = useState('en');
  const t = (key, vars) => {
    let s = (I18N[lang] && I18N[lang][key]) || I18N.en[key] || key;
    if (vars) s = s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? vars[k] : m));
    return s;
  };
  const switchLang = (l) => {
    setLang(l);
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [oh, setOh] = useState(null);

  // registration state
  const [reg, setReg] = useState({ id_token: '', gmail: '', name: '', email: '', phone: '' });
  const [sessionToken, setSessionToken] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [registering, setRegistering] = useState(false);
  const [registerMsg, setRegisterMsg] = useState('');

  // chat state
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatting, setChatting] = useState(false);
  const composingRef = useRef(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    fetch(`${apiBase()}/openhouse/${slug}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => { setOh(d); setLoading(false); })
      .catch(e => { setError(t('notFound')); setLoading(false); });
  }, [slug]);

  // Restore saved session (下次免註冊：同一裝置自動解鎖)
  useEffect(() => {
    if (!slug) return;
    try {
      const raw = localStorage.getItem(sessionKey(slug));
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved || !saved.token) return;
      const claims = decodeJwt(saved.token);
      if (claims.exp && Date.now() / 1000 > claims.exp) {
        localStorage.removeItem(sessionKey(slug));
        return;
      }
      setSessionToken(saved.token);
      setBuyerName(saved.name || '');
    } catch (e) {}
  }, [slug]);

  // Google Identity Services
  useEffect(() => {
    if (!oh || !oh.google_client_id) return;
    const init = () => {
      if (!window.google || !window.google.accounts) return;
      window.google.accounts.id.initialize({
        client_id: oh.google_client_id,
        callback: (resp) => {
          const j = decodeJwt(resp.credential);
          setReg(prev => ({ ...prev, id_token: resp.credential, gmail: j.email || '' }));
          setRegisterMsg('');
        },
      });
      const el = document.getElementById('gsi-btn');
      if (el) {
        window.google.accounts.id.renderButton(el, {
          theme: 'outline', size: 'large', shape: 'pill', text: 'continue_with', locale: 'en', width: 300,
        });
      }
    };
    if (window.google && window.google.accounts) { init(); return; }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = init;
    document.head.appendChild(s);
  }, [oh]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading) return <div style={styles.center}><p>{t('loading')}</p></div>;
  if (error) return <div style={styles.center}><h2>{error}</h2></div>;

  const p = oh.property;
  const a = oh.agent;

  const doRegister = async () => {
    const phone = reg.phone.trim();
    if (!phone) { setRegisterMsg(t('phoneRequired')); return; }
    if (!reg.id_token && !reg.email.trim()) { setRegisterMsg(t('signInRequired')); return; }
    setRegistering(true);
    setRegisterMsg('');
    try {
      const body = reg.id_token
        ? { id_token: reg.id_token, phone }
        : { email: reg.email.trim(), name: reg.name.trim(), phone };
      const r = await fetch(`${apiBase()}/openhouse/${slug}/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || t('registerFail'));
      setSessionToken(d.session_token);
      setBuyerName(d.name || '');
      setRegisterMsg('');
      try {
        localStorage.setItem(sessionKey(slug), JSON.stringify({ token: d.session_token, name: d.name || '', email: d.email || '' }));
      } catch (e) {}
    } catch (e) {
      setRegisterMsg(t('registerFail'));
    } finally {
      setRegistering(false);
    }
  };

  const resetSession = () => {
    setSessionToken('');
    setBuyerName('');
    setMessages([]);
    try { localStorage.removeItem(sessionKey(slug)); } catch (e) {}
  };

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || chatting) return;
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatInput('');
    setChatting(true);
    try {
      const r = await fetch(`${apiBase()}/openhouse/${slug}/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken, message: msg }),
      });
      const d = await r.json();
      if (!r.ok) {
        if (r.status === 401) { resetSession(); throw new Error(t('sessionExpired')); }
        throw new Error(d.detail || t('botBusy'));
      }
      setMessages(prev => [...prev, { role: 'bot', text: d.reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'bot', text: t('botError') }]);
    } finally {
      setChatting(false);
    }
  };

  const onChatKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (composingRef.current || e.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    sendChat();
  };

  const registered = !!sessionToken;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Language switcher */}
        <div style={styles.langBar}>
          <button type="button" onClick={() => switchLang('en')} style={lang === 'en' ? styles.langActive : styles.langInactive}>English</button>
          <button type="button" onClick={() => switchLang('zh')} style={lang === 'zh' ? styles.langActive : styles.langInactive}>中文</button>
        </div>

        {/* Header */}
        <div style={styles.hero}>
          {a.photo_url ? <img src={a.photo_url} alt={a.name} style={styles.avatar} /> : null}
          <div>
            <div style={styles.agentName}>{a.name}</div>
            <div style={styles.agentTitle}>{a.title || t('licenseTitle')}</div>
          </div>
        </div>

        {/* House */}
        <h1 style={styles.addr}>{p.address}</h1>
        <div style={styles.city}>{p.city}, {p.state} {p.zip}</div>
        <div style={styles.price}>{fmtPrice(p.price, p.currency)}</div>
        <div style={styles.specs}>
          <span>{t('specBeds', { n: p.bedrooms })}</span><span>·</span>
          <span>{t('specBaths', { n: p.bathrooms })}</span><span>·</span>
          <span>{t('specSqft', { n: p.sqft })}</span>
          {p.year_built ? (<><span>·</span><span>{p.year_built}</span></>) : null}
        </div>

        {/* Open house time */}
        {oh.open_house && oh.open_house.start && (
          <div style={styles.ohTime}>
            🏠 {t('ohLabel')}: {new Date(oh.open_house.start).toLocaleString(lang === 'zh' ? 'zh-TW' : 'en-US')}
            {oh.open_house.end ? ` – ${new Date(oh.open_house.end).toLocaleTimeString(lang === 'zh' ? 'zh-TW' : 'en-US')}` : ''}
          </div>
        )}

        {/* Description teaser */}
        <p style={styles.desc}>{(p.description || '').slice(0, 260)}{(p.description || '').length > 260 ? '…' : ''}</p>

        {!registered ? (
          /* ── Registration step ── */
          <div style={styles.regBox}>
            <div style={styles.regTitle}>{t('regTitle')}</div>
            <div style={styles.regSub}>{t('regSub')}</div>

            {oh.google_client_id ? (
              <>
                <div id="gsi-btn" style={styles.gsiBtn}></div>
                {reg.gmail ? <div style={styles.signedAs}>{t('signedAs', { email: reg.gmail })}</div> : null}
              </>
            ) : (
              <>
                <input style={styles.input} placeholder={t('namePh')} value={reg.name}
                  onChange={e => setReg({ ...reg, name: e.target.value })} />
                <input style={styles.input} placeholder={t('emailPh')} type="email" value={reg.email}
                  onChange={e => setReg({ ...reg, email: e.target.value })} />
              </>
            )}

            <input style={styles.input} placeholder={t('phonePh')} type="tel"
              value={reg.phone} onChange={e => setReg({ ...reg, phone: e.target.value })} />

            <button style={styles.primaryBtn} onClick={doRegister} disabled={registering}>
              {registering ? t('registering') : t('registerBtn')}
            </button>
            {registerMsg ? <div style={styles.err}>{registerMsg}</div> : null}
            <div style={styles.smallNote}>{t('consent', { name: a.name })}</div>
          </div>
        ) : (
          /* ── Unlocked: full details + bot ── */
          <>
            <div style={styles.unlockedBanner}>
              <span>{buyerName ? t('welcomeBack', { name: buyerName }) : t('unlocked')}</span>
              <button type="button" onClick={resetSession} style={styles.signoutBtn}>{t('signout')}</button>
            </div>

            {p.features && p.features.length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.h3}>{t('highlights')}</h3>
                <ul style={styles.list}>
                  {p.features.map((f, i) => <li key={i} style={styles.li}>{f}</li>)}
                </ul>
              </div>
            )}

            {p.images && p.images.length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.h3}>{t('photos')}</h3>
                <div style={styles.gallery}>
                  {p.images.slice(0, 6).map((img, i) => (
                    <a key={i} href={img} target="_blank" rel="noreferrer" style={styles.galleryLink}>
                      <img src={img} alt="home" style={styles.galleryImg} />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div style={styles.section}>
              <h3 style={styles.h3}>{t('contact')}</h3>
              {p.listing_url ? <a href={p.listing_url} target="_blank" rel="noreferrer" style={styles.link}>{t('redfin')}</a> : null}
              {p.ig_post_url ? <div style={styles.contactRow}><a href={p.ig_post_url} target="_blank" rel="noreferrer" style={styles.link}>{t('igPost')}</a></div> : null}
              {p.video_url ? <div style={styles.contactRow}><a href={p.video_url} target="_blank" rel="noreferrer" style={styles.link}>{t('video')}</a></div> : null}
              {a.phone ? <div style={styles.contactRow}><a href={`tel:${a.phone}`} style={styles.link}>{t('call', { name: a.name })}</a></div> : null}
            </div>

            {/* Bot */}
            <div style={styles.section}>
              <h3 style={styles.h3}>{t('chatTitle')}</h3>
              <div style={styles.chatBox}>
                {messages.length === 0 && (
                  <div style={styles.chatEmpty}>{t('chatEmpty')}</div>
                )}
                {messages.map((m, i) => (
                  <div key={i} style={m.role === 'user' ? styles.msgUser : styles.msgBot}>
                    <div style={m.role === 'user' ? styles.bubbleUser : styles.bubbleBot}>{m.text}</div>
                  </div>
                ))}
                {chatting && <div style={styles.msgBot}><div style={styles.bubbleBot}>…</div></div>}
                <div ref={chatEndRef} />
              </div>
              <div style={styles.chatInputRow}>
                <textarea
                  style={styles.chatInput}
                  rows={1}
                  placeholder={t('chatPh')}
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={onChatKeyDown}
                  onCompositionStart={() => { composingRef.current = true; }}
                  onCompositionEnd={() => { setTimeout(() => { composingRef.current = false; }, 0); }}
                />
                <button style={styles.sendBtn} onClick={sendChat} disabled={chatting || !chatInput.trim()}>{t('send')}</button>
              </div>
            </div>
          </>
        )}
      </div>

      <div style={styles.footer}>{t('footer', { name: a.name })}</div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f4f5f7', fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif', padding: '16px 12px 40px' },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', fontFamily: 'sans-serif' },
  card: { maxWidth: 520, margin: '0 auto', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,.08)' },
  langBar: { display: 'flex', justifyContent: 'flex-end', gap: 6, padding: '12px 20px 0' },
  langInactive: { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 999, padding: '5px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  langActive: { background: '#0e7a5f', color: '#fff', border: '1px solid #0e7a5f', borderRadius: 999, padding: '5px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  hero: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px 4px' },
  avatar: { width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', background: '#e2e8f0' },
  agentName: { fontWeight: 700, fontSize: 18, color: '#1a202c' },
  agentTitle: { fontSize: 13, color: '#64748b' },
  addr: { fontSize: 22, fontWeight: 800, color: '#0f172a', padding: '12px 20px 0', margin: 0 },
  city: { fontSize: 14, color: '#475569', padding: '2px 20px 0' },
  price: { fontSize: 24, fontWeight: 800, color: '#0e7a5f', padding: '10px 20px 0' },
  specs: { display: 'flex', gap: 6, alignItems: 'center', padding: '6px 20px 0', fontSize: 14, color: '#334155', flexWrap: 'wrap' },
  ohTime: { margin: '12px 20px 0', background: '#ecfdf5', color: '#065f46', padding: '10px 12px', borderRadius: 10, fontSize: 13, fontWeight: 600 },
  desc: { padding: '14px 20px 0', fontSize: 14.5, lineHeight: 1.65, color: '#334155', margin: 0 },
  regBox: { margin: '18px 20px 20px', border: '1px solid #e2e8f0', borderRadius: 14, padding: 18, background: '#f8fafc' },
  regTitle: { fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 4 },
  regSub: { fontSize: 13, color: '#64748b', marginBottom: 14, lineHeight: 1.5 },
  gsiBtn: { display: 'flex', justifyContent: 'center', marginBottom: 12, minHeight: 44 },
  signedAs: { fontSize: 13, color: '#0e7a5f', marginBottom: 12, fontWeight: 600 },
  input: { width: '100%', boxSizing: 'border-box', padding: '12px 14px', fontSize: 15, borderRadius: 10, border: '1px solid #cbd5e1', marginBottom: 10, outline: 'none' },
  primaryBtn: { width: '100%', padding: '14px', fontSize: 16, fontWeight: 700, color: '#fff', background: '#0e7a5f', border: 'none', borderRadius: 12, cursor: 'pointer' },
  err: { color: '#dc2626', fontSize: 13, marginTop: 10 },
  smallNote: { fontSize: 11.5, color: '#94a3b8', marginTop: 10, lineHeight: 1.5 },
  unlockedBanner: { margin: '16px 20px 0', background: '#0e7a5f', color: '#fff', padding: '12px 14px', borderRadius: 10, fontSize: 14, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 },
  signoutBtn: { background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  section: { padding: '18px 20px 0' },
  h3: { fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 10px' },
  list: { margin: 0, paddingLeft: 18 },
  li: { fontSize: 14, color: '#334155', lineHeight: 1.7 },
  gallery: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 },
  galleryLink: { display: 'block' },
  galleryImg: { width: '100%', height: 120, objectFit: 'cover', borderRadius: 10, background: '#e2e8f0' },
  link: { color: '#0e7a5f', fontWeight: 600, fontSize: 14, textDecoration: 'none' },
  contactRow: { marginTop: 8 },
  chatBox: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, background: '#f8fafc', minHeight: 120, maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 },
  chatEmpty: { fontSize: 13, color: '#94a3b8', lineHeight: 1.6 },
  msgUser: { display: 'flex', justifyContent: 'flex-end' },
  msgBot: { display: 'flex', justifyContent: 'flex-start' },
  bubbleUser: { background: '#0e7a5f', color: '#fff', padding: '9px 13px', borderRadius: 14, borderBottomRightRadius: 4, maxWidth: '80%', fontSize: 14, whiteSpace: 'pre-wrap' },
  bubbleBot: { background: '#fff', border: '1px solid #e2e8f0', color: '#1e293b', padding: '9px 13px', borderRadius: 14, borderBottomLeftRadius: 4, maxWidth: '88%', fontSize: 14, whiteSpace: 'pre-wrap' },
  chatInputRow: { display: 'flex', gap: 8, marginTop: 10 },
  chatInput: { flex: 1, boxSizing: 'border-box', padding: '11px 12px', fontSize: 15, borderRadius: 10, border: '1px solid #cbd5e1', outline: 'none', resize: 'none' },
  sendBtn: { padding: '0 18px', fontSize: 15, fontWeight: 700, color: '#fff', background: '#0e7a5f', border: 'none', borderRadius: 10, cursor: 'pointer' },
  footer: { textAlign: 'center', fontSize: 11.5, color: '#94a3b8', marginTop: 16, lineHeight: 1.5 },
};
